import {toPng} from 'html-to-image';
import {buildGoogleFontEmbedCss} from './build-google-font-embed-css';

export interface DownloadElementAsPngOptions {
  pixelRatio?: number;
  backgroundColor?: string;
  /**
   * When true, never embed web fonts (fallback typography in PNG).
   * When false/omitted, embeds fonts when `fontEmbedCSS` or `googleFontEmbed` is set.
   */
  skipFonts?: boolean;
  /** Precomputed `@font-face` CSS with embedded font files (optional). */
  fontEmbedCSS?: string;
  /** Fetch and embed a Google Font family by weight (CORS-safe, no cssRules). */
  googleFontEmbed?: {family: string; weights: number[]};
}

function isLoadableImageUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) {
    return false;
  }
  if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
    return true;
  }
  try {
    const {protocol} = new URL(trimmed, window.location.href);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

function isSvgUrl(url: string): boolean {
  return /\.svg(\?|#|$)/i.test(url);
}

async function fetchImageAsDataUrl(url: string): Promise<string | null> {
  try {
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-store',
      headers: {
        Accept: 'image/svg+xml,image/*,*/*',
      },
    });
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    return await new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () =>
        resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function loadImageViaCanvas(url: string): Promise<string | null> {
  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    const finish = (value: string | null) => {
      img.onload = null;
      img.onerror = null;
      resolve(value);
    };

    img.onload = () => {
      try {
        const width = img.naturalWidth || img.width;
        const height = img.naturalHeight || img.height;
        if (!width || !height) {
          finish(null);
          return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          finish(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        finish(canvas.toDataURL('image/png'));
      } catch {
        finish(null);
      }
    };

    img.onerror = () => finish(null);
    img.src = url;
  });
}

/**
 * Loads an image for PNG export. Requires CORS on cross-origin assets.
 * Tries canvas rasterization first; SVGs and failures fall back to fetch → data URL.
 */
export async function preloadImageAsDataUrl(
  url: string,
): Promise<string | null> {
  const trimmed = url.trim();
  if (!isLoadableImageUrl(trimmed)) {
    return null;
  }

  if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
    return trimmed;
  }

  if (isSvgUrl(trimmed)) {
    const fromFetch = await fetchImageAsDataUrl(trimmed);
    if (fromFetch) {
      return fromFetch;
    }
  }

  const fromCanvas = await loadImageViaCanvas(trimmed);
  if (fromCanvas) {
    return fromCanvas;
  }

  return fetchImageAsDataUrl(trimmed);
}

function triggerDownload(dataUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  link.click();
}

/**
 * Renders a DOM subtree to PNG and triggers a file download.
 */
export async function downloadElementAsPng(
  element: HTMLElement,
  filename: string,
  options?: DownloadElementAsPngOptions,
): Promise<void> {
  const pixelRatio = options?.pixelRatio ?? 2;
  const backgroundColor = options?.backgroundColor ?? '#ffffff';

  const width = Math.ceil(element.scrollWidth || element.offsetWidth);
  const height = Math.ceil(element.scrollHeight || element.offsetHeight);
  if (!width || !height) {
    throw new Error('Export element has zero dimensions');
  }

  await document.fonts.ready;

  let fontEmbedCSS = options?.fontEmbedCSS;
  if (
    options?.skipFonts !== true &&
    !fontEmbedCSS &&
    options?.googleFontEmbed
  ) {
    const {family, weights} = options.googleFontEmbed;
    try {
      const css = await buildGoogleFontEmbedCss(family, weights);
      if (css.trim()) {
        fontEmbedCSS = css;
      }
    } catch {
      // Fall back to skipFonts when font fetch fails.
    }
  }

  const skipFonts = options?.skipFonts ?? !fontEmbedCSS;

  const dataUrl = await toPng(element, {
    width,
    height,
    pixelRatio,
    backgroundColor,
    cacheBust: true,
    skipFonts,
    fontEmbedCSS,
  });

  const safeName = filename.endsWith('.png') ? filename : `${filename}.png`;
  triggerDownload(dataUrl, safeName);
}
